import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { MessageSquare, Send, Crown, Trash2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { btn } from "@/components/Page";
import ConfirmDialog from "@/components/ConfirmDialog";

export function AllianceChat({ isLeader }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const scrollRef = useRef(null);
  const atBottomRef = useRef(true);
  const didInitRef = useRef(false);

  // Scrolls ONLY the chat container to the latest message — never the page (no scrollIntoView).
  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const load = useCallback(() => api.get("/alliance/chat").then((r) => setMsgs(r.data.messages)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    if (!scrollRef.current || !msgs.length) return;
    // Always pin to the newest message on first load; afterwards only if the user is near the bottom.
    if (!didInitRef.current) {
      didInitRef.current = true;
      requestAnimationFrame(scrollToBottom);
    } else if (atBottomRef.current) {
      requestAnimationFrame(scrollToBottom);
    }
  }, [msgs]);

  const onScroll = (e) => {
    const el = e.target;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const { data } = await api.post("/alliance/chat", { text: t });
      atBottomRef.current = true;
      setMsgs((m) => [...m, data]);
      setText("");
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const canDelete = isLeader || user?.is_admin;
  const remove = (id) => setConfirm({
    title: "Delete message",
    message: "Delete this message? This cannot be undone.",
    confirmLabel: "Delete", danger: true,
    onConfirm: async () => {
      try {
        await api.delete(`/alliance/chat/${id}`);
        setMsgs((m) => m.filter((x) => x.id !== id));
      } catch (err) { toast.error(apiErr(err)); }
      setConfirm(null);
    },
  });

  return (
    <section className="border border-amber-900/30 bg-zinc-950 p-5" data-testid="alliance-chat">
      <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3 flex items-center gap-2">
        <MessageSquare size={16} /> War Room <span className="text-zinc-600 normal-case tracking-normal text-xs">(members only)</span>
      </h3>
      <div ref={scrollRef} onScroll={onScroll} data-testid="alliance-chat-messages" className="h-64 overflow-y-auto space-y-2 mb-3 pr-1">
        {msgs.length === 0 ? (
          <p className="text-sm text-zinc-600 italic">No messages yet. Rally your alliance and strategise.</p>
        ) : msgs.map((m) => {
          if (m.system) {
            return (
              <div key={m.id} className="flex justify-center" data-testid={`alliance-chat-system-${m.id}`}>
                <span className="text-[11px] text-amber-300/70 italic bg-amber-950/15 border border-amber-900/30 rounded-sm px-2.5 py-1 text-center">{m.text}</span>
              </div>
            );
          }
          const mine = m.user_id === user?.user_id;
          const isBlocked = m.is_blocked === true;
          return (
            <div key={m.id} className={`group flex flex-col ${mine ? "items-end" : "items-start"}`}>
              <div className={`flex items-center gap-1.5 ${mine ? "flex-row-reverse" : "flex-row"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-sm text-sm ${
                  isBlocked ? "bg-zinc-950 border border-zinc-800 text-zinc-600 italic" :
                  mine ? "bg-amber-900/30 border border-amber-800/40 text-amber-100" : 
                  "bg-zinc-900 border border-zinc-800 text-zinc-200"
                }`}>
                  {!mine && <div data-testid={`alliance-chat-name-${m.id}`} className={`text-[11px] font-cinzel mb-0.5 inline-flex items-center gap-1 ${
                    m.is_leader ? "text-amber-300 font-bold drop-shadow-[0_0_5px_rgba(251,191,36,0.45)]" : 
                    "text-amber-400/80"
                  }`}>
                    {m.is_leader && <Crown size={10} className="text-amber-500" />}
                    {m.name}
                  </div>}
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                </div>
                {canDelete && (
                  <button data-testid={`alliance-chat-delete-${m.id}`} onClick={() => remove(m.id)} title="Delete message"
                    className="text-zinc-700 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"><Trash2 size={13} /></button>
                )}
              </div>
              <span className="text-[10px] text-zinc-600 mt-0.5">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="flex gap-2">
        <input data-testid="alliance-chat-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Message your alliance…"
          className="flex-1 bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 text-sm focus:border-amber-600 outline-none" />
        <button data-testid="alliance-chat-send" type="submit" disabled={busy || !text.trim()} className={btn("primary") + " px-4"}><Send size={15} /></button>
      </form>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </section>
  );
}
